import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { API_URL } from '../config/api'
import StickerConfigurator from '../components/StickerConfigurator'
import ProductDetailModal from '../components/ProductDetailModal'
import './Shop.css'

function getImageUrl(image) {
  if (!image) return null
  if (Array.isArray(image.data)) return image.data[0]?.attributes?.url
  return image.data?.attributes?.url || null
}

export default function Shop() {
  const { token, user } = useAuth()
  const navigate = useNavigate()

  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [cart, setCart] = useState([])
  const [cartOpen, setCartOpen] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', street: '', postal_code: '', city: '' })
  const [step, setStep] = useState('cart')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [configuratorProduct, setConfiguratorProduct] = useState(null)
  const [detailProduct, setDetailProduct] = useState(null)
  const [minStickerPrice, setMinStickerPrice] = useState(null)

  useEffect(() => {
    fetch(`${API_URL}/api/products?populate=*&sort=id:asc`)
      .then(r => r.json())
      .then(d => setProducts(d.data || []))
      .catch(() => setError('Producten konden niet worden geladen.'))
      .finally(() => setLoading(false))

    fetch(`${API_URL}/api/sticker-sizes?sort=order:asc`)
      .then(r => r.json())
      .then(d => {
        const prices = (d.data || []).map(s => Number((s.attributes || s).price))
        if (prices.length) setMinStickerPrice(Math.min(...prices))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (user) setForm(f => ({ name: f.name || user.username || '', email: f.email || user.email || '' }))
  }, [user])

  // Price of a cart item: custom sticker uses size price, regular uses product price
  const itemPrice = (item) =>
    item.customization ? item.customization.size.price : item.product.attributes.price

  const cartTotal = cart.reduce((s, i) => s + itemPrice(i) * i.quantity, 0)
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0)

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      // Only merge non-customized duplicates
      const hit = prev.find(i => i.product.id === product.id && !i.customization)
      if (hit) return prev.map(i => (i.product.id === product.id && !i.customization) ? { ...i, quantity: i.quantity + quantity } : i)
      return [...prev, { product, quantity, customization: null }]
    })
    setCartOpen(true)
  }

  const addCustomToCart = (item) => {
    // Each custom sticker is always a separate line item
    setCart(prev => [...prev, item])
    setCartOpen(true)
  }

  const updateQty = (index, qty) => {
    if (qty <= 0) setCart(prev => prev.filter((_, i) => i !== index))
    else setCart(prev => prev.map((item, i) => i === index ? { ...item, quantity: qty } : item))
  }

  const handleCheckout = async (e) => {
    e.preventDefault()
    if (!token) { navigate('/login'); return }
    setSubmitting(true)
    setError(null)
    try {
      // Upload any sticker images first
      const itemsWithUrls = await Promise.all(cart.map(async (item) => {
        const base = {
          id: item.product.id,
          name: item.product.attributes.name,
          price: itemPrice(item),
          quantity: item.quantity,
        }
        if (!item.customization) return base

        const fd = new FormData()
        fd.append('files', item.customization.imageFile, item.customization.imageName)
        const upRes = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        })
        const uploaded = upRes.ok ? await upRes.json() : []
        return {
          ...base,
          customization: {
            size: item.customization.size.label,
            dimensions: item.customization.size.dimensions,
            imageUrl: uploaded[0]?.url || null,
          },
        }
      }))

      // Initiate Mollie payment — order is created only after payment confirmation
      const res = await fetch(`${API_URL}/api/orders/initiate-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          customer_name: form.name,
          customer_email: form.email,
          items: itemsWithUrls,
          total_cents: Math.round(cartTotal * 100),
          billing_address: {
            street: form.street,
            postal_code: form.postal_code,
            city: form.city,
            country: 'BE',
          },
        }),
      })
      if (!res.ok) throw new Error('Betaling starten mislukt')
      const { checkoutUrl, paymentId } = await res.json()

      // Store payment ID so the result page can look up the order
      localStorage.setItem('pendingPaymentId', paymentId)

      setCart([])
      setCartOpen(false)
      window.location.href = checkoutUrl
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="shop-page">
      {/* Sticker configurator modal */}
      {configuratorProduct && (
        <StickerConfigurator
          product={configuratorProduct}
          onAdd={addCustomToCart}
          onClose={() => setConfiguratorProduct(null)}
        />
      )}

      {/* Product detail modal */}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          minStickerPrice={minStickerPrice}
          inCart={cart.find(i => i.product.id === detailProduct.id && !i.customization)}
          onAddToCart={(quantity) => addToCart(detailProduct, quantity)}
          onConfigure={() => setConfiguratorProduct(detailProduct)}
          onClose={() => setDetailProduct(null)}
        />
      )}

      {/* Cart sidebar overlay */}
      {cartOpen && <div className="cart-overlay" onClick={() => setCartOpen(false)} />}

      {/* Cart sidebar */}
      <aside className={`cart-sidebar${cartOpen ? ' cart-sidebar--open' : ''}`}>
        <div className="cart-sidebar__header">
          <h2>Winkelwagen {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}</h2>
          <button className="cart-close" onClick={() => { setCartOpen(false); setStep('cart') }}>✕</button>
        </div>

        {cart.length === 0 ? (
          <p className="cart-empty">Je winkelwagen is leeg.</p>
        ) : step === 'cart' ? (
          <>
            <ul className="cart-items">
              {cart.map((item, index) => {
                const attr = item.product.attributes
                const imgUrl = item.customization?.imagePreview || (getImageUrl(attr.image) ? `${API_URL}${getImageUrl(attr.image)}` : null)
                return (
                  <li key={index} className="cart-item">
                    {imgUrl
                      ? <img src={imgUrl} alt={attr.name} className={`cart-item__img${item.customization ? ' cart-item__img--sticker' : ''}`} />
                      : null
                    }
                    <div className="cart-item__info">
                      <span className="cart-item__name">
                        {attr.name}
                        {item.customization && <span className="cart-item__tag">Die-cut {item.customization.size.label}</span>}
                      </span>
                      <span className="cart-item__price">€{(itemPrice(item) * item.quantity).toFixed(2)}</span>
                    </div>
                    <div className="cart-item__qty">
                      <button onClick={() => updateQty(index, item.quantity - 1)}>−</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQty(index, item.quantity + 1)}>+</button>
                    </div>
                  </li>
                )
              })}
            </ul>
            <div className="cart-footer">
              <div className="cart-total">
                <span>Totaal</span>
                <strong>€{cartTotal.toFixed(2)}</strong>
              </div>
              <button className="btn-primary btn-full" onClick={() => setStep('details')}>
                Doorgaan →
              </button>
            </div>
          </>
        ) : (
          <form className="checkout-form" onSubmit={handleCheckout}>
            <button type="button" className="btn-back-link" onClick={() => setStep('cart')}>← Terug</button>
            <h3>Jouw gegevens</h3>
            {error && <p className="form-error">{error}</p>}
            <label>
              Naam
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Voor- en achternaam" />
            </label>
            <label>
              E-mailadres
              <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jouw@email.com" />
            </label>
            <label>
              Straat + huisnummer
              <input required value={form.street} onChange={e => setForm(f => ({ ...f, street: e.target.value }))} placeholder="Kerkstraat 12" />
            </label>
            <div className="form-row-split">
              <label>
                Postcode
                <input required value={form.postal_code} onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))} placeholder="9000" />
              </label>
              <label>
                Gemeente
                <input required value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Gent" />
              </label>
            </div>
            <div className="cart-total" style={{ marginTop: '1rem' }}>
              <span>Totaal</span>
              <strong>€{cartTotal.toFixed(2)}</strong>
            </div>
            <button type="submit" className="btn-primary btn-full btn-mollie" disabled={submitting}>
              {submitting ? 'Bezig...' : '🔒 Betalen met Mollie'}
            </button>
            {!token && <p className="form-hint">Je wordt gevraagd in te loggen.</p>}
          </form>
        )}
      </aside>

      {/* Page header */}
      <div className="shop-hero">
        <h1>Onze Producten</h1>
        <p>Kies uit ons assortiment en betaal veilig via Mollie</p>
        {cartCount > 0 && (
          <button className="cart-trigger" onClick={() => setCartOpen(true)}>
            🛒 Winkelwagen ({cartCount}) — €{cartTotal.toFixed(2)}
          </button>
        )}
      </div>

      {/* Products */}
      {loading ? (
        <div className="shop-loading">
          <div className="spinner" />
          <p>Producten laden...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="shop-empty">
          <p>Momenteel zijn er geen producten beschikbaar. Kom binnenkort terug!</p>
        </div>
      ) : (
        <div className="products-grid">
          {products.map(product => {
            const attr = product.attributes
            const imgUrl = getImageUrl(attr.image)
            const isCustomizable = !!attr.customizable
            return (
              <article key={product.id} className="product-card" onClick={() => setDetailProduct(product)}>
                {isCustomizable && <div className="product-card__badge">✂️ Die-cut</div>}
                <div className="product-card__img-wrap">
                  {imgUrl
                    ? <img src={`${API_URL}${imgUrl}`} alt={attr.name} />
                    : <div className="product-card__placeholder">🏷️</div>
                  }
                  <span className="product-card__view-hint">Bekijk product →</span>
                </div>
                <div className="product-card__body">
                  <h3>{attr.name}</h3>
                  <span className="product-price">
                    {isCustomizable
                      ? (minStickerPrice != null ? `Vanaf €${minStickerPrice.toFixed(2)}` : 'Vanaf...')
                      : `€${(attr.price || 0).toFixed(2)}`}
                  </span>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {/* Floating cart button */}
      {cartCount > 0 && !cartOpen && (
        <button className="cart-fab" onClick={() => setCartOpen(true)}>
          🛒 <span>{cartCount}</span>
        </button>
      )}
    </div>
  )
}
